import { useMutation, useQuery } from "@tanstack/react-query";
import { Popover } from "radix-ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { ActionRequired } from "~/foundation/components/ActionRequired";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";
import { depths } from "~/foundation/styles/depths";
import { fetchTldr, listChanges } from "./commandApi";
import { appliedLine, screenContextFor, visibleText } from "./commandModel";
import type { TldrResult } from "./commandTypes";

// "put some sort of tldr button in all screens that when clicked tells the tldr version of whatever this is — with
// tldr insights" (owner, 2026-10-21). One header button; on press the page's visible words + what the route names
// (an item, a project, a document) go to the small model with the Chief's verdict and the workspace's knowledge
// distillate behind them. Under the TL;DR: the last few things AI changed here, from the ledger.

export function TldrButton({ pathname, search = "" }: { pathname: string; search?: string }) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<TldrResult | null>(null);
  const tldr = useMutation({
    mutationFn: () => {
      const context = screenContextFor(pathname, search);
      return fetchTldr(api, {
        ...context,
        title: typeof document === "undefined" ? undefined : document.title,
        text: visibleText(),
      });
    },
    onSuccess: setResult,
  });
  const changes = useQuery({
    queryKey: ["ai", "command", "changes", 5],
    queryFn: () => listChanges(api, 5),
    enabled: open,
    staleTime: 30_000,
  });
  // a new screen is a new TL;DR
  useEffect(() => setResult(null), [pathname, search]);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && !result && !tldr.isPending) tldr.mutate();
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t("TL;DR of this screen")}
          title={t("TL;DR of this screen")}
          className="flex h-8 items-center rounded-sm px-2 font-semibold text-small text-text-secondary tracking-wide hover:bg-hover-bg hover:text-text data-[state=open]:bg-hover-bg data-[state=open]:text-text"
        >
          TL;DR
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          className="slate-pop flex w-[440px] max-w-[92vw] flex-col gap-3 rounded-md border border-divider bg-menu-bg p-3 shadow-menu"
          style={{ zIndex: depths.menu }}
          data-testid="tldr-popover"
        >
          <div className="flex items-center justify-between gap-2">
            <Text variant="body" className="font-semibold">
              {t("TL;DR")}
            </Text>
            <Button
              size="sm"
              variant="ghost"
              disabled={tldr.isPending}
              onClick={() => tldr.mutate()}
            >
              {tldr.isPending ? t("Reading…") : t("Refresh")}
            </Button>
          </div>
          {tldr.isPending && !result ? (
            <Text variant="small">{t("Reading what is on the screen…")}</Text>
          ) : null}
          {tldr.error ? (
            <ActionRequired
              error={
                tldr.error instanceof Error ? tldr.error.message : t("Could not read this screen.")
              }
              onRetry={() => tldr.mutate()}
              onRecheck={() => tldr.mutate()}
              busy={tldr.isPending}
            />
          ) : null}
          {result ? (
            <>
              <p className="text-body text-text">{result.tldr}</p>
              {result.insights.length > 0 ? (
                <ul className="list-disc ps-5 text-small text-text">
                  {result.insights.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : null}
              {result.basis.length > 0 ? (
                <Text variant="small" className="text-text-tertiary">
                  {t("Based on: {{basis}}", { basis: result.basis.join(", ") })}
                </Text>
              ) : null}
            </>
          ) : null}
          <div className="flex flex-col gap-1 border-divider border-t pt-2">
            <Text variant="small" className="font-medium">
              {t("What AI changed here")}
            </Text>
            {changes.data && changes.data.length === 0 ? (
              <Text variant="small" className="text-text-tertiary">
                {t("Nothing yet.")}
              </Text>
            ) : null}
            <ul className="flex flex-col gap-1">
              {(changes.data ?? []).map((c) => (
                <li key={c.id} className="flex flex-col text-small">
                  <span className="text-text">{c.tldr ?? c.text}</span>
                  <span className="text-text-tertiary">
                    {new Date(c.at).toLocaleString()} · {appliedLine(c.applied)}
                    {c.by.name ? ` · ${c.by.name}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
